import { useState } from 'react';
import { api, formatDate, formatMoney, plural, type Specimen, type Valuation } from '../api.ts';
import { Icon } from './Icon.tsx';

/**
 * Деньги образца: цена покупки и журнал оценок (ТЗ 4.4 пункт 7, D1–D2).
 *
 * Цена покупки записывается один раз и живёт в форме образца. Оценка —
 * запись в журнале: в карточке видна последняя, по нажатию раскрывается
 * вся история.
 *
 * В режиме «без цен» сервер не присылает сюда ни одной суммы, и блок не
 * рисуется вовсе — ни заголовка, ни кнопки «добавить оценку». Экран без
 * данных не может проговориться.
 */
export function MoneyBlock({
  specimen,
  onChanged,
}: {
  specimen: Specimen;
  onChanged: () => void;
}) {
  const [history, setHistory] = useState<Valuation[] | null>(null);
  const [adding, setAdding] = useState(false);

  if (specimen.prices_hidden) return null;

  const purchase = specimen.purchase_amount;
  const latest = specimen.latest_valuation;
  const count = specimen.valuation_count ?? 0;

  async function openHistory() {
    if (history) {
      setHistory(null);
      return;
    }
    const response = await api.valuations(specimen.id);
    setHistory(response.valuations);
  }

  return (
    <>
      <div className="label-key">Деньги</div>

      <dl className="rows" style={{ marginTop: 0 }}>
        {purchase !== null && (
          <div className="row">
            <dt>
              <Icon name="tag" className="icon icon-sm" />
              Куплен за
            </dt>
            <dd>{formatMoney(purchase, specimen.purchase_currency)}</dd>
          </div>
        )}
        <div className="row">
          <dt>
            <Icon name="tag" className="icon icon-sm" />
            Оценка
          </dt>
          <dd>
            {latest ? (
              <>
                {formatMoney(latest.amount, latest.currency)}
                <div className="hint">
                  {formatDate(latest.valued_on)}
                  {latest.basis ? ` · ${latest.basis}` : ''}
                </div>
              </>
            ) : (
              <span className="muted">не оценивался</span>
            )}
          </dd>
        </div>
      </dl>

      <div className="chips">
        <button type="button" className="chip chip--plain" onClick={() => setAdding((v) => !v)}>
          {adding ? 'отмена' : latest ? 'новая оценка' : 'оценить'}
        </button>
        {count > 1 && (
          <button type="button" className="chip chip--plain" onClick={openHistory}>
            {history
              ? 'свернуть историю'
              : `вся история · ${count} ${plural(count, 'оценка', 'оценки', 'оценок')}`}
          </button>
        )}
      </div>

      {adding && (
        <ValuationForm
          specimen={specimen}
          onSaved={() => {
            setAdding(false);
            setHistory(null);
            onChanged();
          }}
        />
      )}

      {history && (
        <div className="valuation-log">
          {history.map((v) => (
            <div key={v.id} className="valuation-row">
              <div className="mono small">{formatDate(v.valued_on)}</div>
              <div className="valuation-amount">{formatMoney(v.amount, v.currency)}</div>
              <div className="hint">{[v.basis, v.note].filter(Boolean).join(' · ') || ' '}</div>
              <button
                type="button"
                className="link-btn link-btn--quiet"
                onClick={async () => {
                  await api.deleteValuation(v.id);
                  setHistory(null);
                  onChanged();
                }}
              >
                удалить
              </button>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

/** Основания оценки из ТЗ 5.10 — подсказками, а не жёстким списком. */
const BASIS_HINTS = ['аналог на выставке', 'оценка дилера', 'прикидка', 'страховая'];

function ValuationForm({
  specimen,
  onSaved,
}: {
  specimen: Specimen;
  onSaved: () => void;
}) {
  const today = new Date().toISOString().slice(0, 10);
  const [amount, setAmount] = useState('');
  const [valuedOn, setValuedOn] = useState(today);
  const [basis, setBasis] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const value = Number(amount.replace(',', '.'));
  const valid = amount.trim() !== '' && Number.isFinite(value) && value >= 0;

  async function save() {
    setBusy(true);
    setError(null);
    try {
      await api.addValuation(specimen.id, {
        amount: value,
        valued_on: valuedOn || undefined,
        currency: specimen.purchase_currency ?? undefined,
        basis: basis.trim() || null,
        note: note.trim() || null,
      });
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось сохранить');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="valuation-form">
      <div className="size-row" style={{ gridTemplateColumns: '1fr 1fr' }}>
        <input
          className="field-input"
          inputMode="decimal"
          placeholder="сумма"
          value={amount}
          autoFocus
          onChange={(e) => setAmount(e.target.value)}
        />
        <input
          className="field-input"
          type="date"
          value={valuedOn}
          max={today}
          onChange={(e) => setValuedOn(e.target.value)}
        />
      </div>
      <input
        className="field-input"
        style={{ marginTop: 'var(--space-2)' }}
        placeholder="на чём основана"
        value={basis}
        onChange={(e) => setBasis(e.target.value)}
      />
      <div className="chips">
        {BASIS_HINTS.map((hint) => (
          <button key={hint} type="button" className="chip chip--plain" onClick={() => setBasis(hint)}>
            {hint}
          </button>
        ))}
      </div>
      <input
        className="field-input"
        style={{ marginTop: 'var(--space-2)' }}
        placeholder="заметка (необязательно)"
        value={note}
        onChange={(e) => setNote(e.target.value)}
      />
      {error && (
        <div className="notice notice--danger" style={{ marginTop: 'var(--space-3)' }}>
          {error}
        </div>
      )}
      <button
        className="btn btn--full"
        style={{ marginTop: 'var(--space-3)' }}
        disabled={!valid || busy}
        onClick={save}
      >
        Записать оценку
      </button>
      <div className="hint centered">
        Прошлые оценки не переписываются — это журнал, а не поле
      </div>
    </div>
  );
}
